import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft, Search, Image as ImageIcon, Edit3, Save, X, Eye, EyeOff,
    Star, Loader2, Upload, Trash2, Plus, Camera, ListChecks, LogOut, Radio
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const CATS = ["burgers", "sides", "drinks", "desserts", "extras", "pizza", "sushi", "salads", "mains"];

export const AdminMenu = () => {
    const navigate = useNavigate();
    const { user, loading: authLoading, logout } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeCat, setActiveCat] = useState("all");
    const [editing, setEditing] = useState(null); // item being edited (modal)

    useEffect(() => {
        if (authLoading) return;
        if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
        fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            // Fetch all menu items (admin)
            const { data } = await api.get("/menu");
            setItems(data || []);
        } catch {
            toast.error("Σφάλμα φόρτωσης");
        } finally {
            setLoading(false);
        }
    };

    const cats = useMemo(() => {
        const s = new Set(items.map((i) => i.category));
        return ["all", ...Array.from(s)];
    }, [items]);

    const filtered = useMemo(() => {
        let out = items;
        if (activeCat !== "all") out = out.filter((i) => i.category === activeCat);
        if (search.trim()) {
            const q = search.toLowerCase();
            out = out.filter((i) => (i.name || "").toLowerCase().includes(q));
        }
        return out;
    }, [items, activeCat, search]);

    const patchItem = async (id, patch) => {
        try {
            const { data } = await api.patch(`/menu/${id}`, patch);
            setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x)));
            toast.success("Αποθηκεύτηκε", { duration: 1200 });
            return true;
        } catch {
            toast.error("Σφάλμα αποθήκευσης");
            return false;
        }
    };

    if (authLoading || loading) {
        return (
            <div className="burger-bg min-h-screen grid place-items-center">
                <Loader2 className="animate-spin text-[var(--burger-orange-2)]" size={36} />
            </div>
        );
    }

    return (
        <div data-testid="admin-menu" className="burger-bg min-h-screen text-white pb-16">
            {/* HEADER */}
            <header className="sticky top-0 z-30 cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 py-3 flex items-center gap-3">
                <button onClick={() => navigate("/admin/command")} data-testid="menu-back" className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-white/10">
                    <ArrowLeft size={15} />
                </button>
                <span className="h-10 w-10 grid place-items-center rounded-xl ember-gradient">
                    <ListChecks size={16} className="text-black" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="font-display text-[10px] tracking-[0.32em] text-white/45">// MENU CMS</p>
                    <h1 className="font-display text-xl tracking-[0.18em]">PRODUCTS · {items.length}</h1>
                </div>
                <button onClick={() => navigate("/admin/command")} data-testid="goto-command" className="hidden sm:grid h-10 w-10 place-items-center rounded-xl bg-white/5 hover:bg-white/10" title="Command center">
                    <Radio size={15} />
                </button>
                <button onClick={() => { logout(); navigate("/admin/login"); }} className="h-10 w-10 grid place-items-center rounded-xl bg-white/5 hover:bg-rose-500/20">
                    <LogOut size={15} />
                </button>
            </header>

            {/* SEARCH + CAT TABS */}
            <section className="px-5 mt-5 space-y-3">
                <div className="flex items-center gap-2 h-12 px-4 rounded-2xl glass-burger">
                    <Search size={14} className="text-white/45" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search products..."
                        data-testid="cms-search"
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
                    />
                </div>
                <div className="flex gap-1 p-1 rounded-full glass-burger overflow-x-auto cinematic-scrollbar">
                    {cats.map((c) => {
                        const on = c === activeCat;
                        const count = c === "all" ? items.length : items.filter((i) => i.category === c).length;
                        return (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`cms-cat-${c}`}
                                className={`relative shrink-0 px-4 h-9 rounded-full font-display text-[10px] tracking-widest transition ${
                                    on ? "text-black" : "text-white/70 hover:text-white"
                                }`}
                            >
                                {on && <motion.span layoutId="cms-pill" className="absolute inset-0 -z-10 rounded-full ember-gradient" />}
                                {c.toUpperCase()} · {count}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* GRID */}
            <section data-testid="cms-grid" className="px-5 mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filtered.map((it) => (
                    <CMSCard
                        key={it.id}
                        item={it}
                        onToggleAvail={() => patchItem(it.id, { available: !it.available })}
                        onToggleFeatured={() => patchItem(it.id, { featured: !it.featured })}
                        onEdit={() => setEditing(it)}
                    />
                ))}
                {filtered.length === 0 && (
                    <p className="sm:col-span-2 lg:col-span-3 xl:col-span-4 text-center py-16 text-white/40">
                        Δεν βρέθηκαν προϊόντα.
                    </p>
                )}
            </section>

            {/* EDIT MODAL */}
            <AnimatePresence>
                {editing && (
                    <EditModal
                        item={editing}
                        onClose={() => setEditing(null)}
                        onSave={async (patch) => {
                            const ok = await patchItem(editing.id, patch);
                            if (ok) setEditing(null);
                        }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const CMSCard = ({ item, onToggleAvail, onToggleFeatured, onEdit }) => (
    <motion.article
        layout
        whileHover={{ y: -3 }}
        data-testid={`cms-item-${item.id}`}
        className={`burger-card glass-burger rounded-2xl overflow-hidden border ${item.available ? "border-white/8" : "border-rose-500/30 opacity-65"} relative`}
    >
        <div className="relative h-32 overflow-hidden">
            <img src={item.image_url || "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=400&q=70"} alt={item.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            {item.featured && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-display tracking-widest bg-[var(--burger-orange)] text-black px-2 py-0.5 rounded-full">
                    <Star size={9} fill="currentColor" /> FEATURED
                </span>
            )}
            {!item.available && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-display tracking-widest bg-rose-500 text-white px-2 py-0.5 rounded-full">
                    <EyeOff size={9} /> SOLD OUT
                </span>
            )}
            <span className="absolute bottom-2 left-2 text-[9px] font-mono uppercase tracking-widest text-white/75 bg-black/55 px-2 py-0.5 rounded-full">{item.category}</span>
        </div>
        <div className="p-3">
            <h3 className="font-display text-base tracking-wide truncate leading-tight">{item.name.toUpperCase()}</h3>
            <p className="text-[10px] text-white/45 line-clamp-1 mt-0.5">{item.description}</p>
            <div className="flex items-center justify-between mt-3">
                <span className="font-display text-xl ember-text">€{item.price.toFixed(2)}</span>
                <div className="flex gap-1.5">
                    <button onClick={onToggleAvail} data-testid={`avail-${item.id}`} title={item.available ? "Hide" : "Show"} className="h-8 w-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 transition">
                        {item.available ? <Eye size={13} className="text-emerald-400" /> : <EyeOff size={13} className="text-rose-400" />}
                    </button>
                    <button onClick={onToggleFeatured} data-testid={`feat-${item.id}`} title="Featured" className="h-8 w-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 transition">
                        <Star size={13} className={item.featured ? "text-[var(--burger-orange-2)]" : "text-white/40"} fill={item.featured ? "currentColor" : "none"} />
                    </button>
                    <button onClick={onEdit} data-testid={`edit-${item.id}`} className="h-8 px-3 rounded-lg ember-gradient text-black flex items-center gap-1 text-[10px] font-display tracking-widest">
                        <Edit3 size={11} /> EDIT
                    </button>
                </div>
            </div>
        </div>
    </motion.article>
);

const EditModal = ({ item, onClose, onSave }) => {
    const [form, setForm] = useState({
        name: item.name, description: item.description, price: item.price,
        category: item.category, image_url: item.image_url || "",
        available: item.available, featured: item.featured,
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);
    const dropRef = useRef(null);

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) { toast.error("Εικόνα < 4MB"); return; }
        setUploading(true);
        try {
            const dataUrl = await new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
            });
            const { data } = await api.post("/admin/upload-image", { data_url: dataUrl });
            setForm((f) => ({ ...f, image_url: data.url }));
            toast.success("Εικόνα φορτώθηκε");
        } catch (e) {
            toast.error("Σφάλμα upload");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    };

    const submit = async () => {
        setSaving(true);
        const patch = {
            name: form.name, description: form.description,
            price: parseFloat(form.price) || 0, category: form.category,
            image_url: form.image_url, available: form.available, featured: form.featured,
        };
        await onSave(patch);
        setSaving(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[800] bg-black/85 backdrop-blur-md grid place-items-center p-4"
            onClick={onClose}
            data-testid="cms-edit-modal"
        >
            <motion.div
                initial={{ y: 30, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.97 }}
                transition={{ duration: 0.35, ease: [0.2,0.9,0.2,1] }}
                onClick={(e) => e.stopPropagation()}
                className="burger-bg w-full max-w-2xl rounded-3xl border border-[var(--burger-orange)]/20 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
            >
                <header className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                        <p className="font-display text-[10px] tracking-[0.32em] text-white/45">// EDIT PRODUCT</p>
                        <h2 className="font-display text-2xl tracking-wide">{item.name.toUpperCase()}</h2>
                    </div>
                    <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10">
                        <X size={16} />
                    </button>
                </header>
                <div className="p-6 grid sm:grid-cols-2 gap-5">
                    {/* IMAGE DROPZONE */}
                    <div
                        ref={dropRef}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={onDrop}
                        onClick={() => fileRef.current?.click()}
                        data-testid="cms-dropzone"
                        className="relative cursor-pointer aspect-[4/3] rounded-2xl overflow-hidden border-2 border-dashed border-white/15 hover:border-[var(--burger-orange)]/50 transition group"
                    >
                        {form.image_url ? (
                            <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 grid place-items-center text-white/40">
                                <ImageIcon size={32} />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-end justify-center pb-3">
                            <span className="text-[10px] font-display tracking-widest text-white opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5">
                                {uploading ? <Loader2 className="animate-spin" size={11} /> : <Camera size={11} />}
                                {uploading ? "UPLOADING..." : "DROP / CLICK TO UPLOAD"}
                            </span>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="cms-image-input" />
                    </div>

                    {/* FIELDS */}
                    <div className="space-y-3">
                        <Field label="Name" testid="cms-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                        <Field label="Price (€)" testid="cms-price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1">Category</p>
                            <select
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="cms-category"
                                className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[var(--burger-orange)]/60"
                            >
                                {CATS.map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <ToggleRow label="Available" value={form.available} onChange={(v) => setForm({ ...form, available: v })} testid="cms-available" />
                            <ToggleRow label="Featured" value={form.featured} onChange={(v) => setForm({ ...form, featured: v })} testid="cms-featured" />
                        </div>
                    </div>

                    <div className="sm:col-span-2">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1">Description</p>
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            data-testid="cms-description"
                            className="w-full p-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[var(--burger-orange)]/60 resize-none"
                        />
                    </div>
                </div>
                <footer className="px-6 py-4 border-t border-white/5 flex items-center justify-between gap-2">
                    <button onClick={onClose} className="px-4 h-11 rounded-xl border border-white/10 hover:border-white/30 font-display text-xs tracking-widest text-white/70">
                        CANCEL
                    </button>
                    <motion.button
                        onClick={submit}
                        whileTap={{ scale: 0.97 }}
                        disabled={saving}
                        data-testid="cms-save"
                        className="magnet-cta primary h-11 px-5 text-xs"
                    >
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={13} /> SAVE</>}
                    </motion.button>
                </footer>
            </motion.div>
        </motion.div>
    );
};

const Field = ({ label, testid, type = "text", value, onChange }) => (
    <div>
        <p className="text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1">{label}</p>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[var(--burger-orange)]/60"
        />
    </div>
);

const ToggleRow = ({ label, value, onChange, testid }) => (
    <button
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`flex items-center justify-between gap-2 px-3 h-11 rounded-xl border transition ${
            value ? "bg-[var(--burger-orange)]/12 border-[var(--burger-orange)]/40 text-[var(--burger-orange-2)]"
                  : "bg-white/5 border-white/10 text-white/55"
        }`}
    >
        <span className="text-[11px] font-display tracking-widest">{label.toUpperCase()}</span>
        <span className={`h-5 w-9 rounded-full transition relative ${value ? "bg-[var(--burger-orange)]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${value ? "left-[18px]" : "left-0.5"}`} />
        </span>
    </button>
);

export default AdminMenu;
